import React, { useState, useEffect } from 'react';
import { Save, Package } from 'lucide-react';
import { X, User } from './icons';
import UserAutoComplete from './UserAutoComplete';
import { consumiblesAPI, usuariosAPI } from '../services/api'; // Asegúrate de tener usuariosAPI expuesto
import { useToast } from '../contexts/ToastContext';

export default function AsignarConsumibleModal({ isOpen, onClose, consumible, onAsignado }) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState(true);

    const [formData, setFormData] = useState({
        usuarioId: '',
        cantidad: 1,
        observaciones: '',
    });

    useEffect(() => {
        const fetchUsuarios = async () => {
            try {
                setLoadingUsuarios(true);
                // Asumiendo que existe un endpoint para obtener lista de empleados/usuarios para asignar
                // Puede ser /nomina/usuarios o /usuarios
                const res = await usuariosAPI.getAllNomina(); // Asegurarse que este método exista en api.js
                setUsuarios(res.data || []);
            } catch (error) {
                console.error('Error cargando usuarios:', error);
                showToast('No se pudieron cargar los usuarios', 'error');
            } finally {
                setLoadingUsuarios(false);
            }
        };

        if (isOpen) {
            fetchUsuarios();
            setFormData({
                usuarioId: '',
                cantidad: 1,
                observaciones: '',
            });
        }
    }, [isOpen, showToast]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.usuarioId) {
            showToast('Debe seleccionar un usuario', 'error');
            return;
        }

        if (formData.cantidad <= 0) {
            showToast('La cantidad debe ser mayor a 0', 'error');
            return;
        }

        if (consumible && formData.cantidad > consumible.stockActual) {
            showToast(`La cantidad excede el stock actual (${consumible.stockActual})`, 'error');
            return;
        }

        try {
            setLoading(true);
            const res = await consumiblesAPI.registrarMovimiento({
                consumibleId: consumible.id,
                tipo: 'SALIDA',
                cantidad: parseInt(formData.cantidad),
                usuarioId: parseInt(formData.usuarioId),
                observaciones: formData.observaciones
            });

            showToast('Insumo asignado exitosamente', 'success');

            // La descarga se gestionará desde el módulo de Actas (Control de Actas)
            // No mostramos prompt de descarga inmediata.

            onAsignado();
            onClose();
        } catch (error) {
            console.error('Error al asignar:', error.message);
            showToast(error.response?.data?.message || 'Error al procesar la asignación', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !consumible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <User className="w-5 h-5 mr-2 text-blue-600" />
                        Asignar Insumo
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 border-b border-blue-100 dark:border-blue-800">
                    <div className="flex items-start">
                        <Package className="w-5 h-5 text-blue-600 mt-1 mr-2" />
                        <div>
                            <p className="font-medium text-blue-900 dark:text-blue-100">{consumible.nombre}</p>
                            <p className="text-sm text-blue-700 dark:text-blue-300">Stock Actual: <strong>{consumible.stockActual}</strong></p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 overflow-y-auto flex-1 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Usuario Destinatario *
                        </label>
                        {loadingUsuarios ? (
                            <p className="text-sm text-gray-500 pl-1">Cargando usuarios...</p>
                        ) : (
                            <UserAutoComplete
                                value={formData.usuarioId}
                                onChange={(val) => setFormData(prev => ({ ...prev, usuarioId: val }))}
                                usuarios={usuarios}
                                placeholder="Buscar usuario por nombre, departamento o email..."
                                required={true}
                            />
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Cantidad a Entregar *
                        </label>
                        <input
                            type="number"
                            name="cantidad"
                            value={formData.cantidad}
                            onChange={handleChange}
                            min="1"
                            max={consumible.stockActual}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Observaciones
                        </label>
                        <textarea
                            name="observaciones"
                            value={formData.observaciones}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Entrega de mouse nuevo por falla del anterior..."
                        />
                    </div>
                </form>

                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || formData.cantidad > consumible.stockActual}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center shadow-sm"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Asignando...' : 'Confirmar Entrega'}
                    </button>
                </div>
            </div>
        </div>
    );
}
